// OrganiseWise Design System Tokens
// This file contains all design tokens used throughout the application

export const designTokens = {
  // Brand Identity
  brand: {
    name: "OrganiseWise",
    tagline: "AI-powered organization",
    logo: {
      icon: "fa-solid fa-brain",
      primaryColor: "#0284c7", // primary-600
    }
  },

  // Colors
  colors: {
    // Light mode primary colors (blue-based)
    primary: {
      50: "#eff6ff",
      100: "#dbeafe", 
      200: "#bfdbfe",
      300: "#93c5fd",
      400: "#60a5fa",
      500: "#3b82f6",
      600: "#2563eb", // Brand primary
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a",
    },
    
    // Light mode secondary colors (purple-based)
    secondary: {
      50: "#faf5ff",
      100: "#f3e8ff",
      200: "#e9d5ff", 
      300: "#d8b4fe",
      400: "#c084fc",
      500: "#a855f7",
      600: "#9333ea",
      700: "#7c3aed",
      800: "#6b21a8",
      900: "#581c87",
    },

    // Dark mode colors
    dark: {
      50: "#0a0e27",   // Darkest background
      100: "#1a2332",  // Card backgrounds
      200: "#1a2332",  // Elevated surfaces
      300: "#1f2937",  // Borders
      400: "#374151",  // Disabled states
      500: "#4b5563",  // Secondary text
      600: "#6b7280",  // Primary text
      700: "#9ca3af",  // Light text
      800: "#d1d5db",  // Lighter text
      900: "#f9fafb",  // Lightest text
    },

    // Dark mode accent colors (purple/violet)
    accent: {
      50: "#1e1b4b",
      100: "#312e81", 
      200: "#3730a3",
      300: "#4338ca",
      400: "#5b21b6",
      500: "#7c3aed",  // Primary accent
      600: "#8b5cf6",
      700: "#a78bfa",
      800: "#c4b5fd",
      900: "#ddd6fe",
    },

    // Dark mode cyan colors
    cyan: {
      50: "#083344",
      100: "#155e75",
      200: "#0891b2", 
      300: "#0e7490",
      400: "#06b6d4",
      500: "#22d3ee",  // Secondary accent
      600: "#67e8f9",
      700: "#a7f3d0",
      800: "#ccfbf1",
      900: "#f0fdfa",
    },

    // Neutral colors (gray scale)
    neutral: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8", 
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },

    // Feedback colors (work in both light and dark)
    feedback: {
      success: "#10b981",
      warning: "#f59e0b", 
      danger: "#ef4444",
      info: "#06b6d4",
    },
    // Semantic colors for easy use
    semantic: {
      background: "#f9fafb", // neutral-50
      surface: "#ffffff",
      border: "#e5e7eb", // neutral-200
      text: {
        primary: "#111827", // neutral-900
        secondary: "#6b7280", // neutral-500
        tertiary: "#9ca3af", // neutral-400
        inverse: "#ffffff",
      }
    }
  },

  // Typography
  typography: {
    fontFamily: {
      primary: ['Inter', 'system-ui', 'sans-serif'],
      mono: ['JetBrains Mono', 'Fira Code', 'monospace'],
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.75',
    },
    // Semantic typography scales
    scale: {
      h1: {
        fontSize: "1.875rem", // 30px
        fontWeight: 700,
        lineHeight: 1.25,
      },
      h2: {
        fontSize: "1.5rem", // 24px
        fontWeight: 600,
        lineHeight: 1.25,
      },
      h3: {
        fontSize: "1.25rem", // 20px
        fontWeight: 500,
        lineHeight: 1.25,
      },
      h4: {
        fontSize: "1.125rem", // 18px
        fontWeight: 500,
        lineHeight: 1.25,
      },
      body: {
        fontSize: "1rem", // 16px
        fontWeight: 400,
        lineHeight: 1.5,
      },
      small: {
        fontSize: "0.875rem", // 14px
        fontWeight: 400,
        lineHeight: 1.5,
      },
      caption: {
        fontSize: "0.75rem", // 12px
        fontWeight: 400,
        lineHeight: 1.5,
      }
    }
  },

  // Spacing Scale
  spacing: {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
  },

  // Border Radius
  borderRadius: {
    none: '0',
    sm: '0.125rem',   // 2px
    default: '0.25rem', // 4px
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    '2xl': '1rem',    // 16px
    full: '9999px',
  },

  // Shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    soft: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    glow: '0 0 20px rgba(139, 92, 246, 0.3)', // Purple glow for dark mode
    'cyan-glow': '0 0 20px rgba(34, 211, 238, 0.3)', // Cyan glow for dark mode
  },

  // Component-specific tokens
  components: {
    button: {
      height: {
        sm: '2rem',     // 32px
        default: '2.5rem', // 40px
        lg: '3rem',     // 48px
      },
      padding: {
        sm: '0.5rem 0.75rem',
        default: '0.5rem 1rem',
        lg: '0.75rem 1.5rem',
      },
      iconSize: {
        sm: "1rem",     // 16px
        default: "1.25rem", // 20px
        lg: "1.5rem",   // 24px
      }
    },
    input: {
      height: {
        sm: '2rem',
        default: '2.5rem',
        lg: '3rem',
      },
      padding: "0.75rem", // 12px
      borderWidth: "1px",
    },
    card: {
      padding: {
        sm: '1rem',
        default: '1.5rem',
        lg: '2rem',
      },
      borderRadius: "0.75rem", // 12px
    },
    modal: {
      maxWidth: "32rem", // 512px
      borderRadius: "0.75rem", // 12px
    }
  },

  // Breakpoints
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },

  // Z-index scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },

  // Animation & Transitions
  animation: {
    duration: {
      fast: '150ms',
      normal: '300ms', 
      slow: '500ms',
    },
    easing: {
      linear: 'linear',
      'ease-in': 'cubic-bezier(0.4, 0, 1, 1)',
      'ease-out': 'cubic-bezier(0, 0, 0.2, 1)',
      'ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
    }
  }
};

// Helper functions for accessing tokens
export const getColor = (path) => {
  const keys = path.split('.');
  let result = designTokens.colors;
  for (const key of keys) {
    result = result[key];
  }
  return result;
};

export const getSpacing = (size) => designTokens.spacing[size];
export const getTypography = (scale) => designTokens.typography.scale[scale];
export const getShadow = (size) => designTokens.shadows[size]; 